import {
	Dialog,
	Form,
	FormItem,
	renderReactDialog,
	TextInput,
	Toolbar,
	ValidationError
} from "client/Components";
import { IFormHandler, useFormHandler } from "client/Forms";
import {
	AlertManager,
	useAlerts,
	useDataInputDialog,
	useUnmountedCancellation
} from "client/Hooks";
import {
	createValidationConfiguration,
	InputValidationResult,
	InputValidationResultKind,
	IRootValidation,
	IValidationConfiguration
} from "client/Validation";
import {
	inline,
	isNotEmpty,
	isNotNullNorUndefined
} from "client/Validation/rules";
import { isValid } from "client_legacy/Documents/WebDav/documentNamePolicy";
import { publish, WebDavEvent } from "client_legacy/Documents/WebDav/events";
import { createFolder, CreateFolderModel } from "client/Api/Documents/WebDav/webDavActions";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { localize } from "client_legacy/ndaw/culture";
import {
	ReactElement,
	useCallback,
	useState
} from "react";
import { FileSystemItemModel } from "client/Api/Documents/WebDav/webDav";

type NewFolderDialogProps = {
	serverId: number;
	folderId: string;
	onClose: () => void;
};

const NewFolderDialog = (props: NewFolderDialogProps): ReactElement => {
	const {
		serverId,
		folderId,
		onClose
	} = props;

	const [isBusy, setIsBusy] = useState<boolean>(false);
	const [formData, setFormData] = useState<FormData>({
		name: ""
	});

	const globalCancellation: AbortSignal = useUnmountedCancellation();
	const alertManager: AlertManager = useAlerts();

	const formHandler: IFormHandler<FormData> = useFormHandler(
		formData,
		setFormData,
		validationConfiguration
	);

	const submitHandler = useCallback(
		async (): Promise<void> => {
			if (isBusy) {
				return;
			}

			setIsBusy(true);
			const validation: IRootValidation<FormData> = formHandler.validation;

			try {
				const validationResult: InputValidationResult = await validation
					.validate(globalCancellation);

				if (validationResult.kind !== InputValidationResultKind.Valid) {
					setIsBusy(false);
					return;
				}

				const model: CreateFolderModel = {
					...formData,
					folderId
				};

				const createdFolder: FileSystemItemModel = await createFolder(
					serverId,
					model,
					globalCancellation
				);

				publish(
					WebDavEvent.CreatedOrReplaced,
					{
						serverId,
						item: createdFolder
					}
				);

				onClose();
			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}

				if (!validation.tryProcessServerError(e)) {
					console.error(e);
					alertManager.showError(localize("_GeneralErrorMessage"));
				}

				setIsBusy(false);
			}
		},
		[
			alertManager,
			folderId,
			formData,
			formHandler.validation,
			globalCancellation,
			isBusy,
			onClose,
			serverId
		]
	);

	return (
		<Dialog
			trackingId="webdav-new-folder-dialog"
			hasBusyIndicator={isBusy}
		>
			<Dialog.Header
				title={localize("_CreateANewFolder")}
				onClose={onClose}
			/>

			<Dialog.Body>
				<Form onSubmit={submitHandler}>
					<FormItem label={localize("_FolderName")} isRequired>
						<TextInput
							{...formHandler.for("name").attachToTextInput()}
							placeholder={localize("_EnterFolderName")}
							autoFocus="focus"
						/>

						<ValidationError {...formHandler.for("name").attachToValidationError()} />
					</FormItem>
				</Form>
			</Dialog.Body>

			<Dialog.Footer>
				<Toolbar>
					<Toolbar.Button
						kind="primary"
						trackingId="done"
						text={localize("_Done")}
						onClick={submitHandler}
					/>

					<Toolbar.Button
						kind="secondary"
						trackingId="cancel"
						text={localize("_Cancel")}
						onClick={onClose}
					/>
				</Toolbar>
			</Dialog.Footer>
		</Dialog>
	);
};
NewFolderDialog.displayName = "NewFolderDialog";

type FormData = Omit<CreateFolderModel, "folderId">;

const validationConfiguration: IValidationConfiguration<FormData> =
	createValidationConfiguration<FormData>();

validationConfiguration.forScalar("name")
	.add(
		isNotNullNorUndefined(),
		isNotEmpty(),
		inline(
			(value): InputValidationResult => {
				if (value !== null) {
					if (!isValid(value)) {
						return {
							kind: InputValidationResultKind.Invalid,
							errorMessage: localize("_ContainsIllegalCharactersForWebDav")
						};
					}

					if (value.trim().endsWith(".")) {
						return {
							kind: InputValidationResultKind.Invalid,
							errorMessage: localize("_CannotEndWithAPeriod")
						};
					}
				}

				return {
					kind: InputValidationResultKind.Valid
				};
			}
		)
	);

export type NewFolderDialogInitialData = Omit<
	NewFolderDialogProps,
	"onClose"
>;

export function useNewFolderDialog(): [
	element: ReactElement | null,
	open: (props: NewFolderDialogInitialData) => Promise<void>
] {
	const [element, originalOpen] = useDataInputDialog<NewFolderDialogInitialData, void>(
		(props, onClose) => (
			<NewFolderDialog
				{...props}
				onClose={onClose}
			/>
		)
	);

	const open = useCallback(
		async (props: NewFolderDialogInitialData): Promise<void> => {
			await originalOpen(props);
		},
		[originalOpen]
	);

	return [element, open];
}

export function openNewFolderDialog(props: Omit<NewFolderDialogProps, "onClose">): void {
	renderReactDialog(
		(onClose) => (
			<NewFolderDialog
				{...props}
				onClose={onClose}
			/>
		)
	);
}
